import React, { useEffect, useState, useMemo, useRef } from 'react';
import { AutoSizer, MultiGrid } from 'react-virtualized';
import { createTheme, ThemeProvider, Switch } from '@material-ui/core';
import { scrollbarSize } from 'dom-helpers';

import styles from './index.module.css';
import AssignmentFirstRow from './components/AssignmentFirstRow';
import AssignmentFirstColumn from './components/AssignmentFirstColumn';
import AssignmentCell from './components/AssignmentCell';
import {
    makeMessage,
    postMessageToParent,
    toLotData,
    toItemData
} from './utils';

import './index.css';
import './scrollbars.scss';
import './normalize.scss';

const theme = createTheme({
    palette: {
        secondary: {
            main: '#2f78c4'
        }
    },
    overrides: {
        MuiCheckbox: {
            root: {
                '&.Mui-disabled': {
                    opacity: 0.5
                }
            }
        }
    }
});

const SupplierAssignment = (props) => {
    const fetchTimeoutId = useRef(-1);
    const [data, setData] = useState({
        isDisabled: false,
        isLive: false
    });

    const {
        auctionType,
        boundaryPrices,
        boundaryPricesFetched,
        currency,
        eventType,
        isDisabled,
        isDisplayLineItems,
        isFloor,
        isLineItemsToggleVisible,
        isLive,
        isLoading,
        localeCode,
        lots,
        sourcingType,
        suppliers,
        takePrice,
    } = props;

    const rows = useMemo(() => {
        if (!lots) {
            return [];
        }
        if (isDisplayLineItems) {
            return lots.reduce(
                (acc, lot, lotIdx) => [
                    ...acc,
                    toLotData(lot, lotIdx),
                    ...lot.items.row_data.map((item, itemIdx) =>
                        toItemData(item, itemIdx, lotIdx)
                    )
                ],
                []
            );
        }

        return lots.map(toLotData);
    }, [lots, isDisplayLineItems]);

    useEffect(() => {
        const isValid = obj =>
            obj && typeof obj === 'object' && Object.keys(obj).length > 0;

        postMessageToParent({
            initAssign: true
        });

        const onAssignment = e => {
            const newData = e.data;
            if (isValid(newData)) {
                setData(oldData => ({
                    ...oldData,
                    ...newData
                }));
            }
        };

        window.addEventListener('message', onAssignment);

        return () => {
            window.removeEventListener('message', onAssignment);
        };
    }, []);

    const toggleDisplayLineItems = () => {
        postMessageToParent(makeMessage('toggleDisplayLineItems'));
    };

    const handleSectionRendered = ({
        rowOverscanStartIndex,
        rowOverscanStopIndex
    }) => {
        if (fetchTimeoutId.current > -1) {
            clearTimeout(fetchTimeoutId.current);
        }

        // Debounce loading
        fetchTimeoutId.current = setTimeout(() => {
            const inspectedRows = rows.slice(
                rowOverscanStartIndex,
                rowOverscanStopIndex + 1
            );
            const data = { lot_id: [], line_items: [] };

            inspectedRows.forEach(row => {
                const key = row.variant === 'lot' ? 'lot_id' : 'line_items';

                if (boundaryPricesFetched[`${row.variant}-${row.id}`]) {
                    return;
                }

                data[key].push(row.id);

                if (row.variant === 'line_item' && lots[row.lotNum - 1]) {
                    const lotId = lots[row.lotNum - 1].id;

                    if (!data.lot_id.includes(lotId)) {
                        data.lot_id.push(lots[row.lotNum - 1].id);
                    }
                }
            });

            if (data.lot_id.length > 0 || data.line_items.length > 0) {
                postMessageToParent(makeMessage('fetchBoundaryPrice', data));
                fetchTimeoutId.current = -1;
            }
        }, 300);
    };

    const cellRenderer = ({ columnIndex, rowIndex, key, style }) => {
        const invitedSupplier = suppliers[columnIndex - 1];
        const isSupplierAddedBeforeLastPublish = (
            isLive
            && invitedSupplier?.status === 'approved'
            && eventType === 'auction'
        );
        if (columnIndex === 0) {
            return (
                <AssignmentFirstColumn
                    key={key}
                    rowIndex={rowIndex}
                    rows={rows}
                    rootStyle={style}
                    isDisplayLineItems={isDisplayLineItems}
                    thStyle={styles.th}
                    suppliers={suppliers}
                    isLoading={isLoading}
                    isDisabled={isDisabled || (isLive && eventType === 'auction')}
                />
            );
        }
        if (rowIndex === 0) {
            return (
                <AssignmentFirstRow
                    rootStyle={style}
                    suppliers={suppliers}
                    key={key}
                    columnIndex={columnIndex}
                    rowIndex={rowIndex}
                    takePrice={takePrice}
                    isDisabled={isDisabled || isSupplierAddedBeforeLastPublish}
                    isFloorPrice={isFloor}
                    isLoading={isLoading}
                    lots={lots}
                />
            );
        }

        return (
            <AssignmentCell
                key={key}
                rootStyle={style}
                columnIndex={columnIndex}
                rowIndex={rowIndex}
                rows={rows}
                lots={lots}
                suppliers={suppliers}
                isDisabled={isDisabled}
                isCheckboxDisabled={isDisabled || isSupplierAddedBeforeLastPublish}
                isLoading={isLoading}
                takePrice={takePrice}
                currency={currency}
                localeCode={localeCode}
                boundaryPrices={boundaryPrices}
            />
        );
    };

    const getRowHeight = ({ index }) => {
        if (index === 0) {
            return 72;
        }
        return 36;
    };

    const getColumnWidth = ({ index }) => {
        if (index === 0) {
            return 275;
        }
        return 246;
    };

    const totalRows = rows.length;
    const totalSuppliers = Array.isArray(suppliers) ? suppliers.length : 0;
    const headerHeight = 72;
    const visibleRows = Math.min(totalRows, 15);
    const contentHeight = visibleRows * 36 + scrollbarSize();

    if (!totalSuppliers || !rows.length) {
        return null;
    }

    return (
        <ThemeProvider theme={theme}>
            {
                isLineItemsToggleVisible ? (
                    <div className={styles.containerSwitch}>
                        <span>{isFloor ? 'Set Floor Price' : 'Set Ceiling Price'} for Line Items</span>
                        <Switch
                            checked={isDisplayLineItems}
                            onChange={toggleDisplayLineItems}
                            color="secondary"
                            name="displayLineItems"
                            disabled={isDisabled}
                        />
                    </div>
                ) : (
                    <div style={{ marginTop: 10 }}></div>
                )
            }
            <div className={styles.container}>
                <AutoSizer disableHeight>
                    {({ width, height }) => {
                        return (
                            <div>
                                <MultiGrid
                                    cellRenderer={cellRenderer}
                                    columnWidth={getColumnWidth}
                                    onSectionRendered={handleSectionRendered}
                                    columnCount={totalSuppliers + 1}
                                    height={headerHeight + contentHeight}
                                    width={width}
                                    rowHeight={getRowHeight}
                                    rowCount={totalRows + 1}
                                    overscanColumnCount={4}
                                    overscanRowCount={4}
                                    fixedColumnCount={1}
                                    fixedRowCount={1}
                                />
                            </div>
                        );
                    }}
                </AutoSizer>
            </div>
        </ThemeProvider>
    );
};

export default SupplierAssignment;
